// TimelineLanes - how the project got here and what is next: sheet issues
// by discipline, milestones, and stage spans, with a Today line. A
// milestone that came from the schedule opens that activity.
import React from "react";
import { timelineRange, toTime } from "./glanceModel";

const LX = 200; const LW = 900; const ROW = 62; const SPAN_ROW = 22;
const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const labelWidth = (text) => text.length * 7 + 10;

// Labels on a busy lane collide (one sheet set issued on the same week as
// another, twenty milestones in a month). Greedy two-track placement:
// above the line, else below, else no label - the diamond keeps its
// tooltip. Returns, per item, "up" | "down" | null.
function placeLabels(items, tx) {
  const ends = { up: -Infinity, down: -Infinity };
  return items.map((it) => {
    const text = clip(it.label, 30) + (it.approx ? " (date approx.)" : "");
    const w = labelWidth(text); const x = tx(it.date);
    for (const track of ["up", "down"]) {
      if (x - w / 2 >= ends[track]) { ends[track] = x + w / 2; return { track, text }; }
    }
    return { track: null, text };
  });
}

export default function TimelineLanes({ timeline, today, onOpenActivity, marker }) {
  const lanes = (timeline && timeline.lanes) || [];
  const range = timelineRange(timeline, today);
  if (!lanes.length || !range) return null;
  const [T0, T1] = range;
  const tx = (d) => LX + ((toTime(d) - T0) / (T1 - T0)) * LW;
  const inRange = (d) => { const t = toTime(d); return t >= T0 && t <= T1; };
  // A lane with several stage spans gives each its own row.
  const laneHeight = (ln) => Math.max(ROW, (ln.spans || []).length * SPAN_ROW + 14);
  const laneY = [];
  let acc = 34;
  lanes.forEach((ln) => { const h = laneHeight(ln); laneY.push(acc + (h - ROW) / 2); acc += h; });
  const height = acc + 30;
  const axisY = height - 26;

  // Month ticks, thinned to quarters or years on long programmes.
  const months = (T1 - T0) / (30 * 24 * 3600 * 1000);
  // Full month names fit about nine to the axis; past that they run
  // together, so go short, then every other month, quarters, years.
  const step = months > 60 ? 12 : months > 24 ? 3 : months > 12 ? 2 : 1;
  const shortNames = months > 9;
  const ticks = [];
  const d = new Date(T0); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + 1);
  while (d.getTime() <= T1) {
    if (d.getUTCMonth() % step === 0) ticks.push(new Date(d));
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const tickLabel = (t) => t.toLocaleString("en-US", { month: shortNames ? "short" : "long", timeZone: "UTC", ...(step > 1 || t.getUTCMonth() === 0 ? { year: "numeric" } : {}) });

  return (
    <svg viewBox={`0 0 1140 ${height}`} role="img" aria-label="Timeline of design issues, milestones and stages">
      {ticks.map((t) => (
        <g key={t.getTime()}>
          <line x1={tx(t)} y1={10} x2={tx(t)} y2={axisY - 12} stroke="var(--line)" strokeWidth="0.6" />
          <text x={tx(t) + 4} y={axisY + 6} fontSize="14" fill="var(--ink2)">{tickLabel(t)}</text>
        </g>
      ))}
      {lanes.map((ln, i) => {
        const y = laneY[i];
        const items = (ln.items || []).filter((it) => inRange(it.date)).sort((a, b) => a.date.localeCompare(b.date));
        const spans = ln.spans || [];
        const placed = placeLabels(items, tx);
        return (
          <g key={ln.id || ln.name}>
            <text x={LX - 14} y={y + 5} textAnchor="end" fontSize="15" fontWeight="500" fill="var(--ink)">
              {clip(ln.name, 26)}<title>{ln.name}</title>
            </text>
            {!spans.length && <line x1={LX} y1={y} x2={LX + LW} y2={y} stroke="var(--line)" strokeDasharray={ln.empty ? "4 5" : undefined} />}
            {ln.empty && !items.length && !spans.length && <text x={LX + 10} y={y - 8} fontSize="14" fill="var(--ink2)">{ln.empty}</text>}
            {spans.map((s, k) => {
              const x1 = Math.max(LX, tx(s.start)); const x2 = Math.min(LX + LW, tx(s.end));
              if (x2 <= x1) return null;
              const stagger = spans.length > 1 ? (k - (spans.length - 1) / 2) * SPAN_ROW : 0;
              return (
                <g key={k}>
                  <rect x={x1} y={y - 9 + stagger} width={x2 - x1} height={18} fill={s.existing ? "var(--roof)" : "var(--meet)"} stroke="var(--line)">
                    <title>{`${s.label}: ${s.start} to ${s.end}${s.detail ? ` (${s.detail})` : ""}`}</title>
                  </rect>
                  {x2 - x1 > 60 && (
                    <text x={x1 + 8} y={y + 5 + stagger} fontSize="14" fill="var(--ink)" style={{ pointerEvents: "none" }}>{clip(s.label, Math.floor((x2 - x1 - 12) / 7))}</text>
                  )}
                </g>
              );
            })}
            {items.map((it, k) => {
              const x = tx(it.date);
              const { track, text } = placed[k];
              const hollow = it.approx || it.done === false;
              const link = it.ref && it.ref.ganttItemId && onOpenActivity;
              return (
                <g key={k} className={`g-ms${link ? " is-link" : ""}`} onClick={link ? () => onOpenActivity(it.ref.ganttItemId) : undefined}
                   tabIndex={link ? 0 : undefined} onKeyDown={link ? (e) => { if (e.key === "Enter") onOpenActivity(it.ref.ganttItemId); } : undefined}>
                  <rect x={x - 6} y={y - 6} width="12" height="12" transform={`rotate(45 ${x} ${y})`}
                        fill={it.major ? "var(--riser)" : hollow ? "var(--sheet)" : "var(--ink)"} stroke="var(--ink)" strokeWidth="1" />
                  {track && (
                    <text x={x} y={track === "up" ? y - 13 : y + 24} textAnchor={x > LX + LW - 80 ? "end" : x < LX + 60 ? "start" : "middle"} fontSize="14" fill="var(--ink)">
                      {text}
                    </text>
                  )}
                  <title>{`${it.label}, ${it.date}${it.done === false ? " (not done yet)" : ""}`}</title>
                </g>
              );
            })}
          </g>
        );
      })}
      {marker && inRange(marker.date) && (
        <g>
          <line x1={tx(marker.date)} y1={4} x2={tx(marker.date)} y2={axisY - 12} stroke="var(--occ)" strokeWidth="2" strokeDasharray="6 4" />
          <text x={tx(marker.date) + 6} y={34} fontSize="14" fontWeight="600" fill="var(--occ)">{marker.label}</text>
        </g>
      )}
      {today && inRange(today) && (
        <g>
          <line x1={tx(today)} y1={4} x2={tx(today)} y2={axisY - 12} stroke="var(--riser)" strokeWidth="2" />
          <text x={tx(today) + 6} y={16} fontSize="14" fontWeight="600" fill="var(--riser)">Today</text>
        </g>
      )}
    </svg>
  );
}
